import {
  GridColumns,
  GridNativeColTypes,
  GridRenderEditCellParams,
  useGridApiContext,
  GridActionsCellItem,
} from '@mui/x-data-grid-pro'
import { useTranslation } from 'react-i18next'
import GenericTable from '../../../../components/table/GenericTable'
import { IComponent } from '../../../../generated-types'
import { Box, useTheme } from '@mui/material'
import BasicDatePicker from '../../../../components/DateTimePicker'
import {
  invalidateComponentsCache,
  useBulkUpdateBatchNumber,
  useBulkUpdateComponentInstallationDate,
  useDeleteComponent,
  useDeleteComponents,
  useUpdateComponent,
  useUpdateComponentInlineValues,
} from '../../../../api/components'
import { invalidatePositionCache } from 'src/api/positions'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import React from 'react'
import { InstallationDateDialog } from '../../workAssignments/component/InstallationDateDialog'
import { UpdateBatchNumberDialog } from '../UpdateBatchNumberDialog'
import { GridActionButton } from '../../../../components/common/GridActionButton'
import { ReactComponent as AddDeviation } from '../../../../assets/svg/addDeviation.svg'
import DesignatedLifetime from './DesignatedLifetime'
import {
  CreatePositionDeviationDialog,
  ICreateDeviationDialogProps,
} from '../../deviations/CreatePositionDeviationDialog'
import { IDeviationType } from '../../../../generated-types/deviation-type'
import { useGetPosition } from '../../../../api/positions'
import { useDialogCtx, useLocationCtx } from '../../../../hooks/context-hooks'
import { DateRangeOutlined, DeleteOutline, Edit, ReportProblemOutlined, AddCircleOutline } from '@mui/icons-material'
import { BatchNumberCell } from 'src/components/batch-numbers/BatchNumberCell'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { Padding } from 'src/styles/paddingComponent'
import { Flex } from 'src/styles/flexComponent'
import { getUTCDate } from 'src/utils/function'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateDeviation } from 'src/api/deviation'
import { invalidateMooringCache } from 'src/api/moorings'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import NoPositionContent from '../../../../assets/svg/no-position-content.svg'
import { TableActionTypes } from 'src/constant'
import { invalidateDocument } from 'src/api/document'
import { hasPermission } from 'src/components/common/Authorization'
import { permissionValue } from 'src/enums/permissionValue'

interface IComponentTable {
  components: IComponent[]
  setComponents: React.Dispatch<React.SetStateAction<IComponent[]>>
  customerId: EntityId
  positionId: EntityId
  localityId: EntityId
  mooringId: EntityId
  height?: string | number
  enableToolbar?: boolean
  onEdit?: (id: number) => void
  onSelectDeviation?: (componentId: number) => void
  onRowClick?: (component: IComponent) => void | undefined
  actionType?: TableActionTypes
}

export const ComponentsTable: React.FC<IComponentTable> = ({
  components,
  customerId,
  positionId,
  localityId,
  mooringId,
  height,
  enableToolbar = true,
  onEdit,
  onSelectDeviation,
  onRowClick,
  actionType = TableActionTypes.Inline,
}) => {
  const { t } = useTranslation(['mooring', 'common', 'position', 'component', 'deviation', 'product'])
  const [selectedRows, setSelectedRows] = useState([] as IComponent[])
  const { mutateAsync } = useUpdateComponentInlineValues(customerId, positionId)
  const { mutateAsync: mutationUpdateInstallationDateBulk, isLoading: isBulkUpdating } =
    useBulkUpdateComponentInstallationDate(customerId, positionId)
  const { mutate } = useDeleteComponent(customerId, positionId)
  const { data: position } = useGetPosition(customerId, localityId as EntityId, mooringId as EntityId, positionId)
  const { mutateAsync: componentBulkDelete } = useDeleteComponents(customerId, positionId)
  const { showDialog, showConfirmDialog, openFilePreviewDialog } = useDialogCtx()
  const { permission } = useLocationCtx()
  const theme = useTheme()
  const { mutateAsync: mutationUpdateComponent } = useUpdateComponent(customerId, positionId)
  const { mutateAsync: mutationUpdateBatchNumber } = useBulkUpdateBatchNumber(customerId as EntityId)

  const ComponentActions = () => {
    return (
      <Flex.Row
        position={'relative'}
        height={0}
        zIndex={100}
        top={20}
        right={10}
        justifyContent={'right'}
        alignItems={'center'}
        p={1}
        gap={1}
      >
        <GridActionButton
          onClick={() => {
            if (selectedRows.length > 0) {
              handleSetInstallationDate()
            } else {
              toast(t('noSelectedComponents'), { type: 'error' })
            }
          }}
          variant="outlined"
          height="33px"
          width="33px"
          buttonIcon={
            <DateRangeOutlined
              htmlColor={selectedRows.length > 0 ? theme.palette.primaryMain[theme.palette.mode] : 'disable'}
            />
          }
          disabled={selectedRows.length == 0 || !hasPermission([permissionValue.Locality.Mooring.Write], permission)}
        />
        <GridActionButton
          onClick={() => handleComponentsBulkDelete()}
          height="33px"
          width="33px"
          variant="outlined"
          buttonIcon={
            <DeleteOutline
              htmlColor={selectedRows.length > 0 ? theme.palette.primaryMain[theme.palette.mode] : 'disable'}
            />
          }
          disabled={selectedRows.length == 0 || !hasPermission([permissionValue.Locality.Mooring.Delete], permission)}
        />
      </Flex.Row>
    )
  }

  const onAddDeviationForComponent = useCallback(
    async (component: IComponent) => {
      const added = await showDialog(
        CreatePositionDeviationDialog,
        {
          componentProps: {
            customerId: customerId as EntityId,
            localityId: localityId as EntityId,
            mooringId: mooringId as EntityId,
            positionId: positionId as EntityId,
            position: position,
            component: component,
            type: IDeviationType.Position,
            disableSteps: true,
          } as ICreateDeviationDialogProps,
        },
        undefined,
        true,
      )
      if (!added) return
    },
    [showDialog, customerId, localityId, mooringId, positionId, position],
  )

  const onDelete = async (id: EntityId) => {
    showConfirmDialog(
      t('deleteComponent', { ns: 'component' }),
      t('areYouSureDeleteComponent', { ns: 'component' }),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        await mutate(id, {
          onSuccess: () => {
            toast.success(t('componentDeleted', { ns: 'component' }))
            invalidateComponentsCache.getComponents(customerId, positionId as EntityId)
            invalidateCustomer.getCustomersActivityCounts(customerId)
            invalidateDeviation.invalidateAllDeviations(customerId, localityId)
            invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
            invalidateMooringCache.getMooringsForMap(customerId, localityId)
            invalidatePositionCache.getBatchNumberDetailsByProductId(customerId, localityId, mooringId)
            invalidateDocument.getDocuments(customerId)
            invalidateMooringCache.getExternalComponentsForMooring(customerId, localityId, mooringId as EntityId)

            pop()
          },
        })
      },
    )
  }

  const onAddBatchNumber = useCallback(
    async (component: IComponent) => {
      const added = await showDialog(
        UpdateBatchNumberDialog,
        {
          opts: {
            maxWidth: 'sm',
            fullWidth: true,
          },
          componentProps: {
            customerId: customerId as EntityId,
            localityId: localityId as EntityId,
            mooringId: mooringId as EntityId,
            productId: component.productId as EntityId,
            positionId: positionId as EntityId,
            componentId: component.id,
            productDescription: component.productDescription,
          },
        },
        undefined,
        true,
      )
      if (!added) return
    },
    [showDialog, customerId, localityId, mooringId, positionId],
  )

  const onAddSingleBatchNumber = async (form: any) => {
    await mutationUpdateBatchNumber(form, {
      onSuccess: () => {
        invalidatePositionCache.getBatchNumberDetailsByProductId(customerId, localityId, mooringId)
        invalidatePositionCache.getPosition(customerId, localityId, mooringId, positionId)
        invalidateComponentsCache.getComponents(customerId, positionId)
        invalidateComponentsCache.getComponent(customerId, positionId, form.componentId)
        toast.success(t('successfullyUpdated', { ns: 'common-api-message' }))
      },
      onError: () => {
        toast.error(t('InternalError', { ns: 'common-api-message' }))
      },
    })
  }

  const onRowsSelectionHandler = useCallback(
    ids => {
      setSelectedRows(components.filter(x => ids.includes(x.id)))
    },
    [components],
  )

  const handleItemInstallationDateChange = useCallback(
    (row: IComponent, date: Date) => {
      if (row) {
        row.installationDate = getUTCDate(date)
        mutateAsync(row, {
          onSuccess: () => {
            invalidateComponentsCache.getComponents(customerId as EntityId, positionId as EntityId)
          },
        })
      }
    },
    [customerId, positionId],
  )

  const handleSetInstallationDate = useCallback(() => {
    showDialog(
      InstallationDateDialog,
      {
        componentProps: {
          onSubmit: (date: Date, pop) => {
            mutationUpdateInstallationDateBulk(
              {
                componentIds: selectedRows.map(x => x.id),
                installationDate: date,
              },
              {
                onSuccess: () => {
                  invalidateComponentsCache.getComponents(customerId as EntityId, positionId as EntityId)
                  pop()
                },
              },
            )
          },
        },
      },
      undefined,
      true,
    )
  }, [selectedRows])

  const handleComponentsBulkDelete = async () => {
    showConfirmDialog(
      t('deleteComponent', { ns: 'component' }),
      t('areYouSureDeleteComponent', { ns: 'component' }),
      undefined,
      async pop => {
        componentBulkDelete(
          { componentIds: selectedRows?.map(x => x.id) as number[] },
          {
            onSuccess: () => {
              invalidateComponentsCache.getComponents(customerId as EntityId, positionId as EntityId)
              invalidateCustomer.getCustomersActivityCounts(customerId)
              invalidateDeviation.invalidateAllDeviations(customerId, localityId)
              invalidateMooringCache.getMooringsForMap(customerId, localityId)
              toast.success(t('deletedSuccessfully', { ns: 'common' }))
              pop()
            },
          },
        )
      },
    )
  }

  const updateComponentInline = useCallback(
    (component: IComponent) => {
      mutationUpdateComponent(component, {
        onSuccess: () => {
         
          invalidateComponentsCache.getComponents(customerId, positionId)
        },
      })
    },
    [customerId, mutationUpdateComponent, positionId],
  )

  const onCellEditCommit = useCallback(
    ({ id, field, value }) => {
      const component = (components || [])?.find(c => c.id === id)
      if (component) {
        if (field === 'quantity') {
          value <= 0 ? toast.error(t('quantityCantBeNegative', { ns: 'component' })) : (component[field] = value)
        } else {
          component[field] = value
        }
        updateComponentInline(component)
      }
    },
    [components],
  )

  const getActionItems = useCallback(
    params => [
      <GridActionsCellItem
        key={`${params.id}-edit`}
        label={t('edit', { ns: 'common' })}
        icon={<Edit />}
        onClick={() => {
          if (onEdit) onEdit(params.id as number)
        }}
        color="primary"
        showInMenu
        disabled={!hasPermission([permissionValue.Locality.Mooring.Write], permission)}
      />,
      <GridActionsCellItem
        key={`${params.id}-add-batch-number`}
        label={t('addBatchNumber', { ns: 'component' })}
        icon={<AddCircleOutline />}
        onClick={() => onAddBatchNumber(params.row)}
        color="primary"
        showInMenu
        disabled={!hasPermission([permissionValue.Locality.Mooring.Write], permission)}
      />,
      <GridActionsCellItem
        key={`${params.id}-add-deviation`}
        label={t('addDeviation', { ns: 'deviation' })}
        icon={<AddDeviation />}
        onClick={() => {
          onAddDeviationForComponent(params.row)
        }}
        color="primary"
        showInMenu
        disabled={!hasPermission([permissionValue.Locality.Deviation.Write], permission)}
      />,
      <GridActionsCellItem
        key={`${params.id}-delete`}
        label={t('delete', { ns: 'common' })}
        icon={<DeleteOutline />}
        onClick={() => onDelete(params.id)}
        color="primary"
        showInMenu
        disabled={!hasPermission([permissionValue.Locality.Mooring.Delete], permission)}
      />,
    ],
    [onAddDeviationForComponent, onEdit, onDelete, onAddBatchNumber, t, permission],
  )

  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      {
        field: 'positionDrawingNumber',
        headerName: '#',
        width: actionType === TableActionTypes.KebabMenu ? 4 : 2,
        sortable: false,
      },
      {
        field: 'productNumber',
        headerName: t('productNumber', { ns: 'component' }),
        minWidth: actionType === TableActionTypes.KebabMenu ? 150 : 135,
        renderCell: params => {
          return (
            <Flex.Row justifyContent={'center'}>
              <Padding.pt4>{params.row?.productNumber}</Padding.pt4>
              {onSelectDeviation && (
                <Padding.pl4>
                  <ReportProblemOutlined
                    className="hover-pointer"
                    sx={{
                      color:
                        params?.row?.deviationCount > 0
                          ? getThemeColor(theme, MUIThemeColors.errorMain)
                          : theme.palette.action.active,
                      opacity: !hasPermission([permissionValue.Locality.Mooring.Deviation.Read], permission) ? 0.38 : 1,
                      cursor: !hasPermission([permissionValue.Locality.Mooring.Deviation.Read], permission)
                        ? 'default'
                        : 'pointer',
                    }}
                    onClick={() => {
                      if (hasPermission([permissionValue.Locality.Mooring.Deviation.Read], permission)) {
                        onSelectDeviation(params.row?.id)
                      }
                    }}
                  />
                </Padding.pl4>
              )}
            </Flex.Row>
          )
        },
      },
      {
        field: 'productDescription',
        headerName: t('productDescription', { ns: 'component' }),
        flex: 2,
        renderCell: params => {
          const isDeviation = params?.row?.deviationCount > 0
          return (
            <span style={{ color: isDeviation ? `${theme.palette.error[theme.palette.mode]}` : '' }}>
              {params.value}
            </span>
          )
        },
      },
      {
        field: 'quantity',
        headerName: t('quantity', { ns: 'component' }),
        minWidth: 20,
        editable: onEdit ? false : true,
      },
      {
        field: 'installationDate',
        headerName: t('installationDate', { ns: 'component' }),
        minWidth: 150,
        type: 'date',
        editable: true,
        renderCell: ({ value }) => {
          return value ? getFormattedDate(value) : '-'
        },
        renderEditCell: onEdit
          ? undefined
          : params => (
              <InstallationDateCell {...params} handleItemInstallationDateChange={handleItemInstallationDateChange} />
            ),
      },
      {
        field: 'designatedLifeTime',
        headerName: t('designatedLifeTime', { ns: 'component' }),
        minWidth: actionType === TableActionTypes.KebabMenu ? 270 : 250,
        renderCell: params => {
          return <DesignatedLifetime row={params.row} />
        },
      },
      {
        field: 'actions',
        headerName: '',
        type: 'actions' as GridNativeColTypes,
        minWidth: actionType === TableActionTypes.KebabMenu ? 50 : 150,
        ...(actionType === TableActionTypes.KebabMenu
          ? { getActions: getActionItems }
          : {
              renderCell: ({ id, row }) => (
                <Flex.Row justifyContent={'center'} alignItems={'center'} gap={1} key={id}>
                  <GridActionButton
                    key={`${id}-add-deviation`}
                    buttonIcon={<AddDeviation color={theme.palette.primaryMain[theme.palette.mode]} />}
                    onClick={() => {
                      onAddDeviationForComponent(row)
                    }}
                    toolTipTitle={t('addDeviation', { ns: 'deviation' })}
                    variant="outlined"
                    shadowWeight={0}
                    height="27px"
                    width="27px"
                    disabled={!hasPermission([permissionValue.Locality.Deviation.Write], permission)}
                  />
                  <GridActionButton
                    key={`${id}-edit`}
                    buttonIcon={<Edit sx={{ color: theme.palette.primaryMain[theme.palette.mode] }} />}
                    hidden={onEdit != undefined}
                    onClick={() => {
                      if (onEdit) onEdit(row.id as number)
                    }}
                    shadowWeight={0}
                    toolTipTitle={t('edit', { ns: 'common' })}
                    variant="outlined"
                    height="27px"
                    width="27px"
                    disabled={!hasPermission([permissionValue.Locality.Mooring.Write], permission)}
                  />
                  <GridActionButton
                    key={`${id}-delete`}
                    buttonIcon={<DeleteOutline htmlColor={theme.palette.primaryMain[theme.palette.mode]} />}
                    onClick={() => onDelete(id)}
                    shadowWeight={0}
                    toolTipTitle={t('delete', { ns: 'common' })}
                    variant="outlined"
                    height="27px"
                    width="27px"
                    disabled={!hasPermission([permissionValue.Locality.Mooring.Delete], permission)}
                  />
                </Flex.Row>
              ),
            }),
      },
    ],
    [handleItemInstallationDateChange, t],
  )
  const getColumns = () => {
    const certificationFilesColumn = {
      field: 'certificationFiles',
      headerName: t('batchNumbers', { ns: 'component' }),
      minWidth: actionType === TableActionTypes.KebabMenu ? 220 : 200,
      renderCell: params => {
        return (
          <BatchNumberCell
            positionId={positionId as number}
            componentId={params?.row?.id as number}
            certificationFiles={params?.row?.certificationFiles}
            onClick={openFilePreviewDialog}
            onAddSingleBatchNumber={onAddSingleBatchNumber}
            disabledAddBatchNumber={!hasPermission([permissionValue.Locality.Mooring.Write], permission)}
          />
        )
      },
    }

    const columns = [...STATIC_COLUMNS]
    columns.splice(5, 0, certificationFilesColumn)
    return columns
  }

  return (
    <Flex.Column bgcolor={getThemeColor(theme, MUIThemeColors.white)} padding={1}>
      <ComponentActions />
      <GenericTable
        id="components"
        data={components as IComponent[]}
        columns={getColumns()}
        onRowClick={onRowClick ? params => onRowClick(params.row) : undefined}
        disableSelectionOnClick={true}
        checkboxSelection={true}
        onSelectionModelChange={ids => onRowsSelectionHandler(ids)}
        enableToolbar={enableToolbar}
        getRowHeight={() => 44}
        height={height}
        autoHeight={!height}
        onCellEditCommit={onEdit ? undefined : onCellEditCommit}
        className={'inner-table'}
        noDataComponent={() => (
          <Box height="100%">
            <EmptyStatusCard
              imageWidth="107px"
              image={NoPositionContent}
              header={t('component-empty-status-title', { ns: 'component' })}
              content={t('component-empty-status-content', { ns: 'component' })}
              headerFontSize="18px"
              contentFontSize="14px"
              contentClasses="mt-2"
              contentTopMargin="10px"
              imageContainerClasses="pb-4 pt-4"
              height="100%"
            />
          </Box>
        )}
      />
    </Flex.Column>
  )
}

interface InstallationDateCellProps extends GridRenderEditCellParams {
  handleItemInstallationDateChange: (row: IComponent, date: Date) => void
}

export const InstallationDateCell: React.FC<InstallationDateCellProps> = params => {
  const { id, value, field, handleItemInstallationDateChange } = params
  const apiRef = useGridApiContext()
  return (
    <Box width={'100%'} py={1}>
      <BasicDatePicker
        name="installationDate"
        onChange={date => {
          apiRef.current.setEditCellValue({ id, field, value: date })
          handleItemInstallationDateChange(params.row, date)
        }}
        value={value}
        isWithoutAppForm={true}
        shouldDisableDate={date => date > new Date()}
      />
    </Box>
  )
}
